import { createUseStyles } from 'react-jss';
import { WrapWithCardOnLargerScreen } from '../components/WrapWithCardOnLargerScreen';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { InnerhivePage } from '../InnerhivePage';
import { useTrackOnVisible } from '../metrics/useTrackOnVisible';
import { HomePageMenu } from './HomePageMenu';
import { useSubscription } from '@meraki-internal/state';
import { HomePageBanner } from './HomePageBanner';
import { CareMapState } from '../caremap/CareMapState';
import { ActiveCareMapProviderV2 } from '../ActiveCareMapProvider';
import { CareRecipientSwitcher } from '../navigation/CareRecipientSwitcher';
import { Recordings } from './Recordings';
import { RecordingState } from '../recording/RecordingState';
import { CaremapNotesModelFactory } from '../notes/model/CaremapNotesModelFactory';
import { RecordingEmptyState } from '../recording/RecordingEmptyState';
import { InsightsHomeSection } from '../insights/InsightsHomeSection';
import { CheckinCard } from '../journal/JournalCard';
import { BeaSonarButton } from '../helper-bee/BeaSonarButton';
import { HelperBeeCard } from '../helper-bee/HelperBeeCard';
import { mediaMinWidth } from '../theme/utils';

const useStyles = createUseStyles({
    page: {
        width: '100%'
    },
    // Anchor the care-recipient switcher as a floating card over the bottom of the video hero,
    // straddling the seam into the content, on every screen size.
    heroWrap: {
        position: 'relative'
    },
    switcherOverlay: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 6,
        zIndex: 5,
        // above md the content card rides 30px up over the hero (WrapWithCardOnLargerScreen's
        // marginTop -30), so lift the switcher fully onto the video instead of onto the card
        [mediaMinWidth.md]: {
            bottom: 44
        }
    },
    // Mirror the content column's geometry exactly (maxWidth + centering + the content padding and
    // ion-card margin below) so the switcher's left edge lines up with the cards to the pixel:
    // 20px on small screens (10 content padding + 10 card margin); 10px above md, where
    // WrapWithCardOnLargerScreen's outer card edge (margin 10) is the visible alignment target.
    switcherColumn: {
        maxWidth: 650,
        margin: '0 auto',
        padding: '0 20px',
        [mediaMinWidth.md]: {
            padding: '0 10px'
        }
    },
    switcherFit: {
        display: 'inline-block'
    },
    // Same vertical placement as BasicHeader's beaButtonWrapper; horizontally the button hugs the
    // content column's right edge (same column band as the switcher) instead of the viewport's.
    // pointer-events pass through the band so only the button itself is tappable.
    beaButtonBand: {
        position: 'absolute',
        top: 'calc(env(safe-area-inset-top, 0px) + 22px)',
        left: 0,
        right: 0,
        zIndex: 100,
        pointerEvents: 'none'
    },
    beaButtonEnd: {
        display: 'flex',
        justifyContent: 'flex-end',
        '& > *': {
            pointerEvents: 'auto'
        }
    },
    content: {
        position: 'relative',
        width: '100%',
        height: '100%',
        maxWidth: 650,
        margin: 'auto'
    }
});

export const CaregiverHomePage: React.FC<{ careMapId?: string;}> = ({ careMapId }) => {
    const classes = useStyles();

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const recordingState = useInstance(RecordingState);
    useSubscription(() => recordingState);

    const notesModelFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesModelFactory);

    // Wait until the page is actually visible (no Welcome slides / Intro overlay
    // on top) before firing this mount-time event.
    useTrackOnVisible(`Caregiver's Corner Opened`);

    // active caremap is needed for both onboarding and checkins
    const activeCareMapId = careMapId || careMapState.getMostRecentCaremap()?.careMapId;

    // Intro card vs tabs: only show intro card once notes have loaded and confirm no recordings.
    // Default to tabs while loading so existing users don't see a flash of the intro card.
    let isIntroFlow = false;
    try {
        const notesModel = notesModelFactory.forActiveCaremap();
        if (notesModel.hasLoaded) {
            isIntroFlow = recordingState.hasNoRecordingsYet();
        }
    } catch {
        // No active caremap yet — default to intro card only if there's genuinely no caremap
        isIntroFlow = !activeCareMapId;
    }

    return (
        <ActiveCareMapProviderV2 careMapId={activeCareMapId}>
            <InnerhivePage hideAppHeader backgroundColor="light">
                <div className={classes.page}>

                    <HomePageMenu />

                    <div className={classes.heroWrap}>
                        <HomePageBanner />
                        <div className={classes.beaButtonBand}>
                            <div className={`${classes.switcherColumn} ${classes.beaButtonEnd}`}>
                                <BeaSonarButton trackingFrom="Welcome" data-id="bea-header-button" />
                            </div>
                        </div>
                        <div className={classes.switcherOverlay}>
                            <div className={classes.switcherColumn}>
                                <div className={classes.switcherFit}>
                                    <CareRecipientSwitcher onImagery />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={classes.content}>
                        <WrapWithCardOnLargerScreen marginTop={-30} marginBottom={30}>
                            <div style={{minHeight: 400, padding: 10}}>
                                {/* Above the intro-flow gate so insights surface for typed-notes-only
                                    caregivers too -- they never leave intro flow (it ends only on the
                                    first recording), yet can accrue insights from typed notes. The
                                    section self-hides when there are no insights, so a brand-new
                                    caregiver still sees just the empty state below. */}
                                <InsightsHomeSection />

                                {isIntroFlow ? (
                                    <RecordingEmptyState />
                                ) : (
                                    <>
                                        <div data-id="home-recording-list" style={{marginBottom: 20}}>
                                            <Recordings hideSwitcher />
                                        </div>

                                        <HelperBeeCard />

                                        <CheckinCard />
                                    </>
                                )}
                            </div>
                        </WrapWithCardOnLargerScreen>
                    </div>

                </div>
            </InnerhivePage>
        </ActiveCareMapProviderV2>
    );
};
